/**
 * The open-estimates list, with the office's inline Approve / Create-job
 * actions.
 *
 * Lifted out of `work-at-site.tsx` (#1011) when that single blended card
 * became three tabs. It MOVES rather than gets rewritten, because what is
 * in here is not a list -- it is the Prompt 22 approve/convert flow with
 * its NEEDS_REVIEW forced match review, and that behaviour is the reason
 * the office works estimates from the location page at all. Rebuilding it
 * "cleanly" would be the way to lose the review gate.
 *
 * 2b doctrine, unchanged and load-bearing: a convert that comes back
 * NEEDS_REVIEW opens the match review dialog. It never attaches silently.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatDollars, cn } from "@/lib/utils";
import { toast } from "sonner";
import { CalendarPlus, CheckCircle2, FileText } from "lucide-react";
import { ConversionReviewDialog } from "@/pages/commercial/conversion-review-dialog";
import { estimateInlineAction, type OpenEstimateRow } from "../location-page-v2";
import { conversionToast, type ConversionScope } from "@/lib/estimate-conversion";

const ESTIMATE_STATUS_LABEL: Record<string, string> = {
    draft: "Draft",
    sent: "Sent",
    accepted: "Approved",
};

interface WorkSummaryResponse {
    openEstimates: OpenEstimateRow[];
}

interface QueueRow {
    id: string;
    match_status: "clean" | "needs_review" | "no_serials";
    matched_device_count: number;
    wo_match: { itemId: string; woNumber: string | null; reason: string } | null;
}

export function OpenEstimatesList({ siteId }: { siteId: string }) {
    const qc = useQueryClient();
    const navigate = useNavigate();
    const [reviewEstimateId, setReviewEstimateId] = useState<string | null>(null);

    const summaryQ = useQuery({
        queryKey: ["site-work-summary", siteId],
        queryFn: () => api.get<WorkSummaryResponse>(`/api/sites/${siteId}/work-summary`),
        enabled: !!siteId,
    });

    // Prompt 22 signals (match confidence, WO auto-attach) for the accepted
    // rows, scoped to this site so the queue lookup stays cheap.
    const queueQ = useQuery({
        queryKey: ["site-conversion-queue", siteId],
        queryFn: () => api.get<{ estimates: QueueRow[] }>(`/api/estimates/conversion-queue?siteId=${siteId}`),
        enabled: !!siteId,
    });
    const queueById = new Map((queueQ.data?.estimates ?? []).map((r) => [r.id, r]));

    const invalidate = () => {
        void qc.invalidateQueries({ queryKey: ["site-work-summary", siteId] });
        void qc.invalidateQueries({ queryKey: ["site-conversion-queue", siteId] });
        void qc.invalidateQueries({ queryKey: ["site-summary", siteId] });
        void qc.invalidateQueries({ queryKey: ["commercial"] });
    };

    // Office approval — same PATCH the estimate detail page uses; the row then
    // gains the convert action on the refetch.
    const approveM = useMutation({
        mutationFn: (estimateId: string) => api.patch(`/api/estimates/${estimateId}`, { status: "accepted" }),
        onSuccess: () => {
            toast.success("Estimate approved — ready to convert");
            invalidate();
        },
        onError: () => toast.error("Couldn't approve the estimate"),
    });

    interface ConvertResponse extends ConversionScope {
        job: { id: string };
        devicesLinked: number;
        woLinked: boolean;
        needsDevices: boolean;
    }

    const convertM = useMutation({
        mutationFn: (estimateId: string) =>
            api.post<ConvertResponse>(`/api/estimates/${estimateId}/convert-to-job`, {}),
        onSuccess: (data) => {
            invalidate();
            const t = conversionToast(data);
            if (t.warn) toast.warning(t.text, { duration: 12000 });
            else toast.success(t.text);
            navigate(`/jobs/${data.job.id}`);
        },
        onError: (e, estimateId) => {
            const body = e instanceof ApiError ? (e.body as { error?: string; message?: string }) : undefined;
            if (body?.error === "NEEDS_REVIEW") {
                // 2b doctrine: never attach silently — drop into match review.
                setReviewEstimateId(estimateId);
                return;
            }
            toast.error(body?.message ?? "Couldn't create the job");
        },
    });

    const estimates = summaryQ.data?.openEstimates ?? [];

    if (summaryQ.isLoading) return <p className="text-sm text-foreground/70">Loading estimates…</p>;
    if (estimates.length === 0) {
        return <p className="text-sm text-foreground/70">No open estimates for this location.</p>;
    }

    return (
        <>
            <ul className="divide-y divide-border rounded-md border border-border">
                {estimates.map((e) => {
                    const queue = queueById.get(e.id);
                    const action = estimateInlineAction(e.status, queue?.match_status);
                    return (
                        <li key={e.id} className="flex items-center gap-3 px-3 py-2">
                            <FileText className="h-4 w-4 shrink-0 text-foreground/60" />
                            <button
                                type="button"
                                onClick={() => navigate(`/commercial/${e.id}?type=estimate`)}
                                className="min-w-0 flex-1 text-left"
                            >
                                <span className="block truncate text-sm">
                                    <span className="font-mono text-xs font-medium text-primary">
                                        {e.display_number ?? `#${e.id.slice(0, 8)}`}
                                    </span>
                                    {e.title ? <span className="text-foreground/80"> — {e.title}</span> : null}
                                </span>
                            </button>
                            <span className="shrink-0 text-sm font-medium tabular-nums">
                                {formatDollars(Number(e.subtotal))}
                            </span>
                            <Badge
                                variant="outline"
                                className={cn(
                                    "shrink-0 text-[10px]",
                                    e.status === "accepted" &&
                                        "border-green-500/50 bg-green-500/10 text-green-700 dark:text-green-300",
                                )}
                            >
                                {ESTIMATE_STATUS_LABEL[e.status] ?? e.status}
                            </Badge>
                            {queue?.wo_match && (
                                <Badge
                                    variant="outline"
                                    className="hidden shrink-0 border-sky-300 bg-sky-50 text-[10px] text-sky-800 sm:inline-flex dark:border-sky-500/40 dark:bg-sky-500/10 dark:text-sky-200"
                                    title={queue.wo_match.reason}
                                >
                                    WO {queue.wo_match.woNumber ?? "match"}
                                </Badge>
                            )}
                            {action === "approve" && (
                                <Button
                                    size="sm"
                                    className="h-7 shrink-0 text-xs"
                                    disabled={approveM.isPending}
                                    onClick={() => approveM.mutate(e.id)}
                                >
                                    <CheckCircle2 className="mr-1 h-3 w-3" /> Approve
                                </Button>
                            )}
                            {(action === "convert" || action === "review_convert") && (
                                <Button
                                    size="sm"
                                    className="h-7 shrink-0 bg-green-700 text-xs text-white hover:bg-green-800"
                                    disabled={convertM.isPending}
                                    onClick={() => {
                                        if (action === "review_convert") setReviewEstimateId(e.id);
                                        else convertM.mutate(e.id);
                                    }}
                                >
                                    <CalendarPlus className="mr-1 h-3 w-3" />
                                    {action === "review_convert" ? "Review & create" : "Create job"}
                                </Button>
                            )}
                        </li>
                    );
                })}
            </ul>

            {reviewEstimateId && (
                <ConversionReviewDialog
                    estimateId={reviewEstimateId}
                    onClose={() => setReviewEstimateId(null)}
                    onConverted={() => invalidate()}
                />
            )}
        </>
    );
}
